<script setup lang="ts">
// Bannière discrète bottom-right qui propose d'installer la PWA.
// - Chrome/Edge : capture beforeinstallprompt, déclenche au 3ᵉ visit + 30s.
// - iOS Safari : redirige vers /aide/installer (pas d'API).
// ADR 0008 §9.

import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { isIOS, isStandalone } from "@/shared/api/push";
import Button from "@/shared/ui/Button.vue";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const VISIT_KEY = "mr_visit_count";
const DISMISSED_KEY = "mr_install_dismissed";
const MIN_VISITS = 3;
const SHOW_AFTER_MS = 30_000;

const router = useRouter();
const promptEvent = ref<BeforeInstallPromptEvent | null>(null);
const visible = ref(false);
let dismissed = false;
let timer: number | undefined;

function incrementVisit(): number {
  const current = parseInt(localStorage.getItem(VISIT_KEY) ?? "0", 10);
  const next = current + 1;
  localStorage.setItem(VISIT_KEY, String(next));
  return next;
}

function shouldOffer(visits: number): boolean {
  if (isStandalone()) return false;
  if (localStorage.getItem(DISMISSED_KEY) === "1") return false;
  return visits >= MIN_VISITS;
}

function onBeforeInstall(e: Event): void {
  e.preventDefault();
  promptEvent.value = e as BeforeInstallPromptEvent;
}

function scheduleVisible(): void {
  timer = window.setTimeout(() => {
    if (!dismissed) visible.value = true;
  }, SHOW_AFTER_MS);
}

async function onInstall(): Promise<void> {
  if (promptEvent.value) {
    await promptEvent.value.prompt();
    await promptEvent.value.userChoice;
    promptEvent.value = null;
  } else if (isIOS()) {
    void router.push("/aide/installer");
  }
  onDismiss();
}

function onDismiss(): void {
  visible.value = false;
  dismissed = true;
  localStorage.setItem(DISMISSED_KEY, "1");
}

onMounted(() => {
  const visits = incrementVisit();
  if (!shouldOffer(visits)) return;
  window.addEventListener("beforeinstallprompt", onBeforeInstall);
  scheduleVisible();
});

onUnmounted(() => {
  window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  if (timer) clearTimeout(timer);
});
</script>

<template>
  <div v-if="visible" class="prompt" role="dialog" aria-label="Installer MailRegent">
    <div class="content">
      <strong>Installer MailRegent ?</strong>
      <p>Accès rapide depuis l'écran d'accueil + notifications push.</p>
    </div>
    <div class="actions">
      <Button variant="primary" size="sm" @click="onInstall">Installer</Button>
      <Button variant="ghost" size="sm" @click="onDismiss">Plus tard</Button>
    </div>
  </div>
</template>

<style scoped>
.prompt {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  background: var(--color-navy-light);
  border: 1px solid rgba(232, 160, 32, 0.4);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: min(320px, calc(100vw - 2rem));
  z-index: 50;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}
.content strong {
  color: var(--color-or);
  font-family: var(--font-serif);
  font-weight: 400;
}
.content p {
  color: var(--color-cream);
  font-size: 0.85rem;
  margin: 0.25rem 0 0 0;
}
.actions {
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
}
</style>
